import { WidgetFlow, WidgetStep } from '@/types'
import { logger, resolveChatEnabled, toaster } from '@/utils'
import { WidgetStepConfigs, getActiveFlowSteps } from '../screen-utils'
import { useFlowState } from '../stores/useFlowState'
import { useRecommendationStore } from '../stores/useRecommendationStore'
import {
  getCurrentGlobalID,
  getWidgetSettings,
  isReviewAuthenticated,
  useSessionStore,
} from '../stores/useSessionStore'
import { useSubmitReviewStore } from '../stores/useSubmitReviewStore'

// =============================================================================
// Types
// =============================================================================

export type StepRedirectResult =
  | { type: 'redirect'; step: WidgetStep; desiredStep: WidgetStep }
  | { type: 'allowed'; step: WidgetStep }

export type NavigationResult =
  | { type: 'success'; nextStep: WidgetStep; previousStep?: WidgetStep }
  | { type: 'blocked'; reason: string }
  | { type: 'skip'; skippedStep: WidgetStep; nextStep: WidgetStep }
  | { type: 'end' }

// =============================================================================
// Step Requirements
// =============================================================================

type StepRequirement = {
  check: () => boolean
  redirectTo: WidgetStep
}

const isChatSettingsEnabled = (): boolean =>
  resolveChatEnabled(useSessionStore.getState().widgetSettings)

const stepRequirements: Partial<Record<WidgetStep, StepRequirement[]>> = {
  [WidgetStep.Chat]: [
    {
      check: isChatSettingsEnabled,
      redirectTo: WidgetStep.Welcome,
    },
  ],
  [WidgetStep.ReviewMyReviews]: [
    {
      check: () => getCurrentGlobalID() !== undefined,
      redirectTo: WidgetStep.SelectStore,
    },
    {
      check: () => isReviewAuthenticated(),
      redirectTo: WidgetStep.ReviewSignInPage,
    },
  ],
  [WidgetStep.ReviewClaimPoints]: [
    {
      check: () => isReviewAuthenticated(),
      redirectTo: WidgetStep.ReviewSignInPage,
    },
  ],
  [WidgetStep.ReviewComplete]: [
    {
      check: () => isReviewAuthenticated(),
      redirectTo: WidgetStep.ReviewSignInPage,
    },
  ],
}

/**
 * Checks if a step has unmet requirements and returns redirect info if needed
 */
export function resolveStepRedirect(targetStep: WidgetStep): StepRedirectResult {
  const requirements = stepRequirements[targetStep]
  if (!requirements) {
    return { type: 'allowed', step: targetStep }
  }

  for (const req of requirements) {
    if (!req.check()) {
      return {
        type: 'redirect',
        step: req.redirectTo,
        desiredStep: targetStep,
      }
    }
  }

  return { type: 'allowed', step: targetStep }
}

/**
 * After review/loyalty OTP, resume an in-progress review submit when we have
 * one. Loyalty and Welcome sign-in have no flow to advance, so enter My
 * Reviews when dash Reviews is on instead of no-op + bouncing back to Sign In.
 */
export function continueAfterReviewSignIn(): void {
  const { currentFlow, previousStep, setCurrentStep, setCurrentFlow, goForward } = useFlowState.getState()
  const returnToClaimOrComplete =
    previousStep === WidgetStep.ReviewClaimPoints || previousStep === WidgetStep.ReviewComplete
  if (currentFlow === WidgetFlow.ProductReview && returnToClaimOrComplete) {
    setCurrentStep(previousStep)
    return
  }
  if (currentFlow === WidgetFlow.ProductReview || currentFlow === WidgetFlow.MyReviews) {
    goForward()
    return
  }

  if (getWidgetSettings()?.reviewSettings?.enabled === true) {
    setCurrentFlow(WidgetFlow.MyReviews)
    return
  }

  setCurrentFlow(undefined)
  setCurrentStep(WidgetStep.Welcome)
}

// =============================================================================
// Flow Initialization
// =============================================================================

export type FlowInitResult = {
  firstStep: WidgetStep
}

/**
 * Determines the initial step when starting a flow
 */
export function getFlowInitialStep(flow: WidgetFlow): FlowInitResult {
  const steps = getActiveFlowSteps(flow)

  switch (flow) {
    case WidgetFlow.Recommendation: {
      const hasSelectedStore = getCurrentGlobalID() !== undefined
      const defaultFirstStep = steps?.[0] ?? WidgetStep.Welcome
      return {
        firstStep: hasSelectedStore ? defaultFirstStep : WidgetStep.SelectStore,
      }
    }
    case WidgetFlow.ProductReview: {
      return {
        firstStep: steps?.[0] ?? WidgetStep.ReviewStars,
      }
    }
    case WidgetFlow.MyReviews: {
      return {
        firstStep: steps?.[0] ?? WidgetStep.ReviewMyReviews,
      }
    }
    case WidgetFlow.Chat: {
      return {
        firstStep: steps?.[0] ?? WidgetStep.Chat,
      }
    }
    default:
      return { firstStep: WidgetStep.Welcome }
  }
}

// =============================================================================
// Navigation Helpers
// =============================================================================

/**
 * Gets the default step for a flow (used as fallback)
 */
export function getDefaultStep(flow: WidgetFlow | undefined): WidgetStep {
  switch (flow) {
    case WidgetFlow.Recommendation: {
      const steps = getActiveFlowSteps(WidgetFlow.Recommendation)
      const hasSelectedStore = getCurrentGlobalID() !== undefined
      if (!hasSelectedStore) return WidgetStep.SelectStore
      return steps[0] ?? WidgetStep.SelectStore
    }
    case WidgetFlow.ProductReview:
      return WidgetStep.ReviewStars
    case WidgetFlow.MyReviews:
      return WidgetStep.ReviewMyReviews
    case WidgetFlow.Chat:
      return WidgetStep.Chat
    default:
      return WidgetStep.Welcome
  }
}

/**
 * Finds the previous step in the flow
 */
export function findPreviousStep(
  currentFlow: WidgetFlow | undefined,
  currentStep: WidgetStep | undefined,
  previousStep: WidgetStep | undefined
): WidgetStep {
  const activeFlowSteps = getActiveFlowSteps(currentFlow)
  const fallbackStep = getDefaultStep(currentFlow)

  if (!activeFlowSteps || activeFlowSteps.length === 0) {
    return fallbackStep
  }

  const effectiveCurrentStep = currentStep ?? fallbackStep
  const isStepFromCurrentFlow = activeFlowSteps.includes(effectiveCurrentStep)

  // If current step is NOT in the flow (e.g., we navigated away),
  // and we have a previousStep saved, go back to that
  // goBack does not update previousStep, so after a back hop it can equal the current step
  if (!isStepFromCurrentFlow && previousStep && previousStep !== effectiveCurrentStep) {
    return previousStep
  }

  const currentIndex = activeFlowSteps.indexOf(effectiveCurrentStep)
  const prevStep = activeFlowSteps[currentIndex - 1]

  return prevStep ?? fallbackStep
}

/**
 * Validates the current step and determines if we can move forward
 */
export function validateStepForForward(currentStep: WidgetStep): {
  canProceed: boolean
  shouldSkip: boolean
  errorMessage?: string
} {
  const stepConfig = WidgetStepConfigs[currentStep]
  const isComplete = stepConfig.isComplete()
  const isRequired = stepConfig.isRequired?.() ?? false

  if (!isComplete && isRequired) {
    return {
      canProceed: false,
      shouldSkip: false,
      errorMessage: 'Must complete all steps before moving on',
    }
  }

  if (!isComplete && !isRequired) {
    return {
      canProceed: true,
      shouldSkip: true,
    }
  }

  return {
    canProceed: true,
    shouldSkip: false,
  }
}

/**
 * Finds the next step in the flow
 */
export function findNextStep(
  currentFlow: WidgetFlow | undefined,
  currentStep: WidgetStep | undefined
): WidgetStep | undefined {
  const activeFlowSteps = getActiveFlowSteps(currentFlow)
  const fallbackStep = getDefaultStep(currentFlow)
  const effectiveCurrentStep = currentStep ?? fallbackStep

  if (!activeFlowSteps || activeFlowSteps.length === 0) {
    return undefined
  }

  const currentIndex = activeFlowSteps.indexOf(effectiveCurrentStep)
  return activeFlowSteps[currentIndex + 1]
}

/**
 * Handles side effects when navigating to a specific step
 */
export function handleStepTransitionSideEffects(nextStep: WidgetStep | undefined): void {
  if (nextStep === WidgetStep.Recommendations) {
    const { loaded } = useRecommendationStore.getState()
    if (!loaded) {
      useRecommendationStore.getState().submitRequest()
    }
  }
  // Fire the review submit on the first terminal step in the review flow so the
  // response (including pointsAwarded) is available before ReviewClaimPoints
  // renders. Skip in-flight, successful, or failed attempts so a failed submit
  // cannot auto-retry by advancing into ReviewComplete.
  if (nextStep === WidgetStep.ReviewClaimPoints || nextStep === WidgetStep.ReviewComplete) {
    const { loading, success, error } = useSubmitReviewStore.getState()
    if (!loading && !success && !error) {
      useSubmitReviewStore.getState().submitReview()
    }
  }
}

/**
 * Shows a toast for navigation errors
 */
export function showNavigationError(message: string): void {
  toaster?.addToast?.({
    type: 'danger',
    message,
    toastKey: 'chat-widget-step-err',
  })
}

// =============================================================================
// High-Level Navigation Functions
// =============================================================================

export type GoBackResult = {
  newStep: WidgetStep
}

/**
 * Computes the result of going back in the flow
 * @param currentFlow - The current flow
 * @param currentStep - The current step
 * @param previousStep - The previous step
 * @returns The result of going back in the flow
 */
export function computeGoBack(
  currentFlow: WidgetFlow | undefined,
  currentStep: WidgetStep | undefined,
  previousStep: WidgetStep | undefined
): GoBackResult {
  const activeFlowSteps = getActiveFlowSteps(currentFlow)

  logger.log('computeGoBack', {
    currentFlow,
    currentStep,
    previousStep,
    activeFlowSteps,
  })

  // If they are on the review my reviews step, we go back to the welcome step
  if (currentFlow === WidgetFlow.MyReviews && currentStep === WidgetStep.ReviewMyReviews) {
    useFlowState.getState().setCurrentFlow(undefined)
    useSubmitReviewStore.getState().reset()
    return { newStep: WidgetStep.Welcome }
  }

  // Chat is a single-step flow; back returns to the welcome screen and clears
  // the active flow so hamburger menu selections work as expected.
  if (currentFlow === WidgetFlow.Chat && currentStep === WidgetStep.Chat) {
    useFlowState.getState().setCurrentFlow(undefined)
    return { newStep: WidgetStep.Welcome }
  }

  // If we're in the product-review flow AND they are on the first step
  // We go to "pre-review" flow + reset the submit review store
  if (currentFlow === WidgetFlow.ProductReview && currentStep === WidgetStep.ReviewStars) {
    useFlowState.getState().setCurrentFlow(WidgetFlow.MyReviews)
    useSubmitReviewStore.getState().reset()
    return { newStep: WidgetStep.ReviewMyReviews }
  }

  // First rec-flow step is not Welcome; exit to the welcome hub like Chat.
  const firstStep = activeFlowSteps?.[0]
  if (currentFlow === WidgetFlow.Recommendation && currentStep === firstStep) {
    useFlowState.getState().setCurrentFlow(undefined)
    return { newStep: WidgetStep.Welcome }
  }

  const newStep = findPreviousStep(currentFlow, currentStep, previousStep)
  return { newStep }
}

export type GoForwardResult =
  | { type: 'success'; nextStep: WidgetStep; shouldSkip: boolean; currentStep: WidgetStep }
  | { type: 'blocked'; reason: string }
  | { type: 'terminal' }

/**
 * Computes the result of going forward in the flow
 * @param currentFlow - The current flow
 * @param currentStep - The current step
 * @returns The result of going forward in the flow
 */
export function computeGoForward(
  currentFlow: WidgetFlow | undefined,
  currentStep: WidgetStep | undefined
): GoForwardResult {
  const activeFlowSteps = getActiveFlowSteps(currentFlow)
  const effectiveCurrentStep = currentStep ?? getDefaultStep(currentFlow)

  logger.log('computeGoForward', { currentFlow, activeFlowSteps, currentStep })

  // Check if we're at a terminal step (can't go forward)
  if (effectiveCurrentStep === WidgetStep.Recommendations) {
    return { type: 'terminal' }
  }

  // Validate current step
  const validation = validateStepForForward(effectiveCurrentStep)
  if (!validation.canProceed) {
    return { type: 'blocked', reason: validation.errorMessage ?? 'Cannot proceed' }
  }

  // Find next step
  const nextStep = findNextStep(currentFlow, currentStep)
  if (!nextStep) {
    return { type: 'terminal' }
  }

  return {
    type: 'success',
    nextStep,
    shouldSkip: validation.shouldSkip,
    currentStep: effectiveCurrentStep,
  }
}
